import { useEffect, useState } from 'react';
import { FEATURES, type FeatureItem } from '@/mocks/entertainment';
import SectionHeading from './SectionHeading';

export default function FeatureReads() {
  const [active, setActive] = useState<FeatureItem | null>(null);
  const [lead, ...rest] = FEATURES;

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(null);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [active]);

  return (
    <section id="feature" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6 lg:px-8">
        <SectionHeading
          eyebrow="深扒专题"
          title="不止吃瓜，更要吃透"
          desc="把热点拆开来看：从事件脉络到人物轨迹，我们用梳理代替断章，让每一口瓜都吃得明白。"
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* lead feature spans full width */}
          <div className="group flex flex-col overflow-hidden rounded-3xl border border-background-200/70 bg-background-50 sm:flex-row lg:col-span-2">
            <div className="relative sm:w-[46%]">
              <img
                src={lead.image}
                alt={`${lead.title} 专题封面`}
                title={`${lead.title} · 深扒专题`}
                className="h-full w-full object-cover object-center sm:min-h-[260px]"
              />
            </div>
            <div className="flex flex-1 flex-col justify-center p-5 md:p-8">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                  {lead.tag}
                </span>
                <span className="font-label text-xs text-foreground-400">{lead.time}</span>
              </div>
              <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                {lead.title}
              </h3>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {lead.excerpt}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setActive(lead)}
                  className="inline-flex items-center whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-opacity hover:opacity-90"
                >
                  完整读完 <i className="ri-arrow-right-line ml-1.5"></i>
                </button>
                <span className="font-label text-xs text-foreground-400">
                  <i className="ri-fire-line text-primary-500"></i> {lead.heat} 人围观
                </span>
              </div>
            </div>
          </div>

          {rest.map((f) => (
            <div
              key={f.id}
              className="group flex flex-col overflow-hidden rounded-3xl border border-background-200/70 bg-background-50"
            >
              <div className="relative aspect-[3/2] overflow-hidden">
                <img
                  src={f.image}
                  alt={`${f.title} 专题封面`}
                  title={`${f.title} · 深扒专题`}
                  className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                  {f.tag}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5 md:p-6">
                <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950">
                  {f.title}
                </h3>
                <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                  {f.excerpt}
                </p>
                <div className="mt-auto flex items-center justify-between pt-4">
                  <span className="font-label text-xs text-foreground-400">
                    <i className="ri-fire-line text-primary-500"></i> {f.heat} 人围观
                  </span>
                  <button
                    type="button"
                    onClick={() => setActive(f)}
                    className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary-600 hover:text-primary-700"
                  >
                    开始阅读 <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* reading overlay */}
      {active && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-foreground-950/60 p-0 sm:items-center sm:p-6"
          onClick={() => setActive(null)}
          role="dialog"
          aria-modal="true"
          aria-label={active.title}
        >
          <div
            className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl bg-background-50 sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-44 w-full shrink-0 md:h-56">
              <img
                src={active.image}
                alt={active.title}
                className="h-full w-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 to-transparent"></div>
              <button
                type="button"
                onClick={() => setActive(null)}
                aria-label="关闭阅读窗口"
                className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-background-50/90 text-foreground-800 transition-colors hover:bg-background-50"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <div className="absolute bottom-4 left-5 right-5 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                  {active.tag}
                </span>
                <span className="font-label text-xs text-background-50/90">
                  深度阅读 · {active.time} · {active.heat} 人围观
                </span>
              </div>
            </div>

            <div className="overflow-y-auto px-5 pb-8 pt-5 md:px-8">
              <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                {active.title}
              </h3>
              <div className="mt-4 flex flex-col gap-4">
                {active.body.map((p, i) => (
                  <p key={i} className="text-[15px] leading-[1.9] text-foreground-700">
                    {p}
                  </p>
                ))}
              </div>
              <div className="mt-6 rounded-2xl bg-background-100/70 p-4 text-sm leading-relaxed text-foreground-600">
                <strong className="font-semibold text-foreground-800">关于本专题：</strong>
                本文基于已公开信息整理，仅用于分享讨论；涉及未证实内容均已标注，如有更新我们会持续跟进。转载需授权。
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}